import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { ArrowLeft, Mail } from 'lucide-react';
import { Link } from 'react-router-dom';
import { BACKEND_URL } from '../config';
import LanguageSwitcher from './LanguageSwitcher';

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [step, setStep] = useState(1); // 1: request, 2: reset

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message);
        // In development, show the token
        if (data.reset_token) {
          setResetToken(data.reset_token);
          setStep(2);
          toast.info(t('auth.fp_dev_token_info', { token: data.reset_token }));
        }
      } else {
        toast.error(data.detail || t('auth.fp_request_failed'));
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('auth.fp_generic_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (response.ok) {
        toast.success(t('auth.fp_reset_success'));
        setTimeout(() => {
          window.location.href = '/login';
        }, 2000);
      } else {
        toast.error(data.detail || t('auth.fp_reset_failed'));
      }
    } catch (error) {
      console.error('Password reset error:', error);
      toast.error(t('auth.fp_generic_error'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-100 via-blue-50 to-indigo-100 p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>
      <div className="w-full max-w-md">
        <Card>
          <CardHeader>
            <Link
              to="/login"
              className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900 mb-4"
              data-testid="forgot-back-link"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              {t('auth.fp_back_to_login')}
            </Link>
            <CardTitle className="text-2xl">
              {step === 1 ? t('auth.fp_request_title') : t('auth.fp_reset_title')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {step === 1 ? (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="email">{t('auth.fp_email_label')}</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-500" />
                    <Input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder={t('auth.email_placeholder')}
                      required
                      className="pl-10"
                      data-testid="forgot-email-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500">
                    {t('auth.fp_email_helper')}
                  </p>
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="forgot-send-link-button"
                >
                  {loading ? t('auth.fp_sending') : t('auth.fp_send_reset_link')}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="token">{t('auth.fp_token_label')}</Label>
                  <Input
                    id="token"
                    type="text"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    placeholder={t('auth.fp_token_placeholder')}
                    required
                    data-testid="forgot-token-input"
                  />
                  <p className="text-xs text-gray-500">
                    {t('auth.fp_token_helper')}
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword">{t('auth.fp_new_password_label')}</Label>
                  <Input
                    id="newPassword"
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('auth.fp_new_password_placeholder')}
                    required
                    minLength={6}
                    data-testid="forgot-new-password-input"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={loading}
                  data-testid="forgot-reset-button"
                >
                  {loading ? t('auth.fp_resetting') : t('auth.fp_reset_button')}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ForgotPassword;
